/**
 * exportNexoComercial — Excel export for SKU → Factura → DIM traceability.
 *
 * Generates a 2-sheet workbook (Resumen + Detalle) using `vista_nexo_comercial`.
 * Designed to satisfy DIAN audit requirements: every inventory item must trace
 * back to its source invoice and the DIM that nationalised it.
 */
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export interface NexoComercialRow {
  item_id: string;
  embarque_id: string;
  numero_do: string | null;
  sku: string | null;
  descripcion: string | null;
  cantidad: number | null;
  unidad_medida: string | null;
  hs_code: string | null;
  costo_unitario_cop: number | null;
  no_factura: string | null;
  fecha_factura: string | null;
  proveedor: string | null;
  dim_archivo_no: string | null;
  numero_dim: string | null;
  numero_levante: string | null;
  fecha_levante: string | null;
}

const sanitizeFilename = (s: string) =>
  (s || "embarque").replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 60);

export interface ExportNexoOptions {
  /**
   * Mapa itemId → costo unitario landed COP proveniente del RPC
   * `calculate_landed_cost_preview` (mismo motor que la trazabilidad).
   * Cuando se provee:
   *   • Sobrescribe `row.costo_unitario_cop` (que en embarques no liquidados
   *     vale 0 porque `base_inventario.costo_nacionalizado_unitario_cop`
   *     solo se popula al liquidar).
   *   • Filtra filas cuyo `item_id` NO esté en el mapa — sirve para excluir
   *     ítems residuales asociados a Packing List / BL que no son comerciales.
   */
  costsByItemId?: Record<string, number>;
  /** Cuando true, agrega banner "PROYECTADO" en el Resumen. */
  isProyectado?: boolean;
}

export async function exportNexoComercial(
  numeroDo: string,
  rows: NexoComercialRow[],
  options: ExportNexoOptions = {},
): Promise<void> {
  const { costsByItemId, isProyectado } = options;

  // Si hay overrides del RPC, descartamos filas no-comerciales (PL/BL) y
  // sustituimos el costo unitario por el valor vivo del motor.
  const effectiveRows: NexoComercialRow[] = costsByItemId
    ? rows
        .filter((r) => Object.prototype.hasOwnProperty.call(costsByItemId, r.item_id))
        .map((r) => ({ ...r, costo_unitario_cop: costsByItemId[r.item_id] ?? 0 }))
    : rows;
  rows = effectiveRows;
  const wb = new ExcelJS.Workbook();
  wb.creator = "Kargonode";
  wb.created = new Date();

  // ── Sheet 1: Resumen ─────────────────────────────────────────────
  const resumen = wb.addWorksheet("Resumen");
  const dim = rows.find((r) => r.numero_dim)?.numero_dim ?? "—";
  const levante = rows.find((r) => r.numero_levante)?.numero_levante ?? "—";
  const fechaLevante = rows.find((r) => r.fecha_levante)?.fecha_levante ?? "—";
  const proveedor = rows.find((r) => r.proveedor)?.proveedor ?? "—";
  const totalItems = rows.length;
  const totalUnidades = rows.reduce((s, r) => s + (r.cantidad ?? 0), 0);
  const totalCosto = rows.reduce(
    (s, r) => s + (r.cantidad ?? 0) * (r.costo_unitario_cop ?? 0),
    0,
  );

  resumen.columns = [
    { width: 32 },
    { width: 42 },
  ];

  const meta: Array<[string, string | number]> = [
    ["NEXO COMERCIAL — KIT ANTISANCIONES DIAN", ""],
    ["", ""],
    ["Embarque (DO)", numeroDo],
    ["Proveedor principal", proveedor],
    ["Número DIM", dim],
    ["Número Levante", levante],
    ["Fecha Levante", fechaLevante],
    ["", ""],
    ["Total ítems (SKUs)", totalItems],
    ["Total unidades", totalUnidades],
    [`Costo total nacionalizado (COP)${isProyectado ? " — PROYECTADO" : ""}`, totalCosto],
    ["", ""],
    ["Generado", new Date().toLocaleString("es-CO")],
  ];

  meta.forEach((row, idx) => {
    const r = resumen.addRow(row);
    if (idx === 0) {
      r.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
      r.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F4C81" } };
      r.alignment = { vertical: "middle", horizontal: "left" };
      resumen.mergeCells(`A${r.number}:B${r.number}`);
      r.height = 28;
    } else if (row[0]) {
      r.getCell(1).font = { bold: true, color: { argb: "FF333333" } };
      r.getCell(2).alignment = { horizontal: "left" };
    }
  });

  // Format costo total as COP
  const costoRow = resumen.getRow(11);
  costoRow.getCell(2).numFmt = '"$"#,##0';

  // ── Sheet 2: Detalle SKU ────────────────────────────────────────
  const detalle = wb.addWorksheet("Detalle SKU");
  const headers = [
    "SKU",
    "Descripción",
    "Cantidad",
    "Unidad",
    "HS Code",
    "No. Factura",
    "Fecha Factura",
    "Proveedor",
    "Costo Unitario (COP)",
    "Costo Total (COP)",
    "No. DIM",
    "No. Levante",
  ];
  detalle.addRow(headers);
  const headerRow = detalle.getRow(1);
  headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
  headerRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF0F4C81" },
  };
  headerRow.alignment = { vertical: "middle", horizontal: "center" };
  headerRow.height = 22;

  rows.forEach((r) => {
    const cantidad = r.cantidad ?? 0;
    const costoUnit = r.costo_unitario_cop ?? 0;
    detalle.addRow([
      r.sku ?? "—",
      r.descripcion ?? "—",
      cantidad,
      r.unidad_medida ?? "—",
      r.hs_code ?? "—",
      r.no_factura ?? "—",
      r.fecha_factura ?? "—",
      r.proveedor ?? "—",
      costoUnit,
      cantidad * costoUnit,
      r.numero_dim ?? "—",
      r.numero_levante ?? "—",
    ]);
  });

  detalle.columns = [
    { width: 16 },
    { width: 38 },
    { width: 10 },
    { width: 10 },
    { width: 14 },
    { width: 18 },
    { width: 14 },
    { width: 26 },
    { width: 18 },
    { width: 18 },
    { width: 16 },
    { width: 16 },
  ];

  // Number formats on data rows
  detalle.getColumn(9).numFmt = '"$"#,##0';
  detalle.getColumn(10).numFmt = '"$"#,##0';
  detalle.getColumn(3).numFmt = "#,##0";

  // Borders
  const lastRow = detalle.rowCount;
  for (let i = 1; i <= lastRow; i++) {
    detalle.getRow(i).eachCell((cell) => {
      cell.border = {
        top: { style: "thin", color: { argb: "FFE5E7EB" } },
        bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
        left: { style: "thin", color: { argb: "FFE5E7EB" } },
        right: { style: "thin", color: { argb: "FFE5E7EB" } },
      };
    });
  }
  detalle.views = [{ state: "frozen", ySplit: 1 }];

  const buf = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `Nexo_Comercial_${sanitizeFilename(numeroDo)}.xlsx`,
  );
}
